// TanStack Query hooks for the Scheduling / Assignments module. An assignment is
// a single guard/EP shift bound to a site (and optionally a post and/or
// principal) that runs through a state machine: open → assigned → confirmed →
// in_progress → completed (with no_show / canceled sinks). Officers are filled
// via a dedicated /assign endpoint, status moves through /transition, and field
// clock punches land in the append-only clock_events log via /clock. Recurrence
// templates and shift swaps are sub-resources. Mirrors
// packages/api/src/routes/assignments.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignAssignmentInput,
  Assignment,
  AssignmentRecurrence,
  AssignmentRecurrenceResponse,
  AssignmentRecurrencesListResponse,
  AssignmentResponse,
  AssignmentStatus,
  AssignmentsListResponse,
  ClockEvent,
  ClockEventResponse,
  ClockEventsListResponse,
  CreateAssignmentInput,
  CreateAssignmentRecurrenceInput,
  CreateClockEventInput,
  CreateShiftSwapInput,
  ShiftSwap,
  ShiftSwapResponse,
  ShiftSwapsListResponse,
  ShiftSwapStatus,
  TransitionAssignmentInput,
  UpdateAssignmentInput,
  UpdateAssignmentRecurrenceInput,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------- Query keys ----------

const KEY = 'assignments';

export const assignmentKeys = {
  all: [KEY] as const,
  lists: () => [KEY, 'list'] as const,
  list: (filters: AssignmentFilters) => [KEY, 'list', { ...filters }] as const,
  detail: (id: number) => [KEY, 'detail', id] as const,
  // Per-shift sub-collections hang off the assignment detail subtree so a
  // single invalidate of detail(id) refreshes clock + swaps for that shift.
  clock: (id: number) => [KEY, 'detail', id, 'clock'] as const,
  swaps: (id: number) => [KEY, 'detail', id, 'swaps'] as const,
  recurrences: (filters: RecurrenceFilters) =>
    [KEY, 'recurrence', { ...filters }] as const,
};

// =============================================================================
// Assignments (core)
// =============================================================================

export interface AssignmentFilters {
  status?: AssignmentStatus;
  assigned_personnel_id?: number;
  site_id?: number;
  unassigned?: boolean;
  /** starts_at >= from (epoch-ms). */
  from?: number;
  /** starts_at <= to (epoch-ms). */
  to?: number;
}

export function useAssignments(filters: AssignmentFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.assigned_personnel_id !== undefined) {
    params.set('assigned_personnel_id', String(filters.assigned_personnel_id));
  }
  if (filters.site_id !== undefined) params.set('site_id', String(filters.site_id));
  if (filters.unassigned) params.set('unassigned', 'true');
  if (filters.from !== undefined) params.set('from', String(filters.from));
  if (filters.to !== undefined) params.set('to', String(filters.to));
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/assignments?${qs}` : '/api/assignments';
  return useQuery({
    queryKey: assignmentKeys.list(filters),
    queryFn: ({ signal }) => api.get<AssignmentsListResponse>(url, signal),
  });
}

export function useAssignment(id: number | null) {
  return useQuery({
    queryKey: assignmentKeys.detail(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<AssignmentResponse>(`/api/assignments/${id}`, signal),
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssignmentInput) =>
      api.post<AssignmentResponse>('/api/assignments', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.lists() });
    },
  });
}

export interface UpdateAssignmentArgs extends UpdateAssignmentInput {
  id: number;
}

export function useUpdateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateAssignmentArgs) =>
      api.patch<AssignmentResponse>(`/api/assignments/${id}`, patch),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.lists() });
      void qc.invalidateQueries({ queryKey: assignmentKeys.detail(id) });
    },
  });
}

/**
 * Advance the shift state machine. Gated server-side on assignment.write; the
 * legal-transition set is enforced by the API (a 409 comes back for an illegal
 * move).
 */
export interface TransitionAssignmentArgs extends TransitionAssignmentInput {
  id: number;
}

export function useTransitionAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: TransitionAssignmentArgs) =>
      api.post<AssignmentResponse>(`/api/assignments/${id}/transition`, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.lists() });
      void qc.invalidateQueries({ queryKey: assignmentKeys.detail(id) });
    },
  });
}

/**
 * Assign (or reassign) an officer to a shift. Gated server-side on
 * assignment.assign — distinct from assignment.write so "fill the shift" can be
 * granted independently of "edit shift metadata".
 */
export interface AssignAssignmentArgs extends AssignAssignmentInput {
  id: number;
}

export function useAssignAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AssignAssignmentArgs) =>
      api.post<AssignmentResponse>(`/api/assignments/${id}/assign`, body),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.lists() });
      void qc.invalidateQueries({ queryKey: assignmentKeys.detail(id) });
    },
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/assignments/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.lists() });
    },
  });
}

// =============================================================================
// Field clock (append-only clock_events for a shift)
//
// distance_m / within_geofence are computed SERVER-SIDE from the site geofence;
// the client only supplies device-reported lat/lng as inputs.
// =============================================================================

export function useClockEvents(assignmentId: number | null) {
  return useQuery({
    queryKey: assignmentKeys.clock(assignmentId ?? 0),
    enabled: assignmentId !== null && assignmentId > 0,
    queryFn: ({ signal }) =>
      api.get<ClockEventsListResponse>(
        `/api/assignments/${assignmentId}/clock`,
        signal,
      ),
  });
}

export interface CreateClockEventArgs extends CreateClockEventInput {
  assignmentId: number;
}

export function useCreateClockEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, ...input }: CreateClockEventArgs) =>
      api.post<ClockEventResponse>(
        `/api/assignments/${assignmentId}/clock`,
        input,
      ),
    onSuccess: (_data, { assignmentId }) => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.clock(assignmentId) });
    },
  });
}

// =============================================================================
// Recurrence templates (the scheduler expands these into concrete shifts)
//
// NB: the list endpoint lives at /api/assignments/recurrence/list and create at
// /api/assignments/recurrence (per the router's sub-resource routing).
// =============================================================================

export interface RecurrenceFilters {
  site_id?: number;
  active?: boolean;
}

export function useRecurrences(filters: RecurrenceFilters = {}) {
  const params = new URLSearchParams();
  if (filters.site_id !== undefined) params.set('site_id', String(filters.site_id));
  if (filters.active !== undefined) params.set('active', String(filters.active));
  const qs = params.toString();
  const url =
    qs.length > 0
      ? `/api/assignments/recurrence/list?${qs}`
      : '/api/assignments/recurrence/list';
  return useQuery({
    queryKey: assignmentKeys.recurrences(filters),
    queryFn: ({ signal }) =>
      api.get<AssignmentRecurrencesListResponse>(url, signal),
  });
}

export function useCreateRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAssignmentRecurrenceInput) =>
      api.post<AssignmentRecurrenceResponse>('/api/assignments/recurrence', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, 'recurrence'] });
    },
  });
}

export interface UpdateRecurrenceArgs extends UpdateAssignmentRecurrenceInput {
  id: number;
}

export function useUpdateRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateRecurrenceArgs) =>
      api.patch<AssignmentRecurrenceResponse>(
        `/api/assignments/recurrence/${id}`,
        patch,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, 'recurrence'] });
    },
  });
}

export function useDeleteRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<void>(`/api/assignments/recurrence/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [KEY, 'recurrence'] });
    },
  });
}

// =============================================================================
// Shift swaps (request → approve | deny)
//
// The list/create endpoints key off the assignment id; approve/deny key off the
// swap id at /api/assignments/swaps/:swapId/{approve,deny}.
// =============================================================================

export function useShiftSwaps(
  assignmentId: number | null,
  status?: ShiftSwapStatus,
) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const qs = params.toString();
  const base = `/api/assignments/${assignmentId}/swaps`;
  const url = qs.length > 0 ? `${base}?${qs}` : base;
  return useQuery({
    queryKey: [...assignmentKeys.swaps(assignmentId ?? 0), { status: status ?? null }] as const,
    enabled: assignmentId !== null && assignmentId > 0,
    queryFn: ({ signal }) => api.get<ShiftSwapsListResponse>(url, signal),
  });
}

export interface CreateShiftSwapArgs extends CreateShiftSwapInput {
  assignmentId: number;
}

export function useCreateShiftSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, ...input }: CreateShiftSwapArgs) =>
      api.post<ShiftSwapResponse>(
        `/api/assignments/${assignmentId}/swaps`,
        input,
      ),
    onSuccess: (_data, { assignmentId }) => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.swaps(assignmentId) });
    },
  });
}

export interface SwapDecisionArgs {
  swapId: number;
  /** The parent assignment, used only to invalidate its swap subtree. */
  assignmentId: number;
}

export function useApproveShiftSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ swapId }: SwapDecisionArgs) =>
      api.post<ShiftSwapResponse>(`/api/assignments/swaps/${swapId}/approve`, {}),
    onSuccess: (_data, { assignmentId }) => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.swaps(assignmentId) });
      void qc.invalidateQueries({ queryKey: assignmentKeys.detail(assignmentId) });
      void qc.invalidateQueries({ queryKey: assignmentKeys.lists() });
    },
  });
}

export function useDenyShiftSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ swapId }: SwapDecisionArgs) =>
      api.post<ShiftSwapResponse>(`/api/assignments/swaps/${swapId}/deny`, {}),
    onSuccess: (_data, { assignmentId }) => {
      void qc.invalidateQueries({ queryKey: assignmentKeys.swaps(assignmentId) });
    },
  });
}

// Re-export the wire types pages commonly need alongside the hooks.
export type {
  Assignment,
  AssignmentRecurrence,
  AssignmentStatus,
  ClockEvent,
  ShiftSwap,
  ShiftSwapStatus,
};
